// USB Installer backend API (documentation/protocol.md "USB Installer"). Identifiers are sent in
// full only in the request bodies that need them; responses carry masked values.
import { getToken } from './api';
import type { DevicePayload, InstallReport, ProvisioningApi, ProvisioningRecord, Resolution, SimPayload } from '../usb/workflow';
import type { AppEventReport } from '../usb/apps';

const BASE = '/api/admin/usb-provisioning';

const headers = (json = true): Record<string, string> => {
  const h: Record<string, string> = json ? { 'Content-Type': 'application/json' } : {};
  const token = getToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

export class UsbApiError extends Error {
  constructor(public code: string, message: string, public resolution?: Resolution) {
    super(message);
    this.name = 'UsbApiError';
  }
}

async function fail(r: Response): Promise<never> {
  const text = await r.text();
  try {
    const body = JSON.parse(text) as { error?: string; resolution?: Resolution };
    if (body.error) throw new UsbApiError(body.error, body.resolution?.reason || body.error, body.resolution);
  } catch (e) {
    if (e instanceof UsbApiError) throw e;
  }
  const m = text.match(/^([A-Z_]+):\s*(.*)$/s);
  throw new UsbApiError(m ? m[1] : `HTTP_${r.status}`, (m ? m[2] : text).trim() || `Request failed (${r.status})`);
}

async function json<T>(r: Response): Promise<T> {
  if (!r.ok) return fail(r);
  return r.json() as Promise<T>;
}

export const resolveUsbRadio = async (device: DevicePayload): Promise<Resolution> =>
  json(await fetch(`${BASE}/resolve`, { method: 'POST', headers: headers(), body: JSON.stringify(device) }));

export const listUsbProvisionings = async (): Promise<ProvisioningRecord[]> =>
  json(await fetch(BASE, { headers: headers(false) }));

export const cancelUsbProvisioning = async (id: string): Promise<ProvisioningRecord> =>
  json(await fetch(`${BASE}/${id}/cancel`, { method: 'POST', headers: headers(false) }));

export interface UsbEvidence {
  usb_provisioning_id: string;
  result: 'EXACT' | 'PARTIAL' | 'CONFLICT' | 'AMBIGUOUS';
  detail: { fields?: Record<string, string>; reasons?: string[]; sim_changed?: boolean };
  status: string;
  model_name: string;
  apk_version_name: string;
  network_name: string;
  provisioned_at: string;
  installed_at: string | null;
}

/** USB provisioning evidence per pending radio (inrico_radios.id), for the approval list. */
export const getUsbEnrollmentEvidence = async (): Promise<Record<string, UsbEvidence[]>> =>
  json(await fetch(`${BASE}/enrollment-evidence`, { headers: headers(false) }));

/** Report-only audit of one radio-app action (queued in the best-effort event log). */
export const reportUsbAppEvent = async (report: AppEventReport): Promise<{ status: string; event_type: string }> =>
  json(await fetch(`${BASE}/app-events`, { method: 'POST', headers: headers(), body: JSON.stringify(report) }));

export const usbProvisioningApi: ProvisioningApi = {
  async create(device: DevicePayload, sims: SimPayload[], confirm: boolean) {
    return json(await fetch(BASE, { method: 'POST', headers: headers(), body: JSON.stringify({ device, sims, confirm }) }));
  },
  async downloadApk(id: string) {
    const r = await fetch(`${BASE}/${id}/apk`, { headers: headers(false) });
    if (!r.ok) return fail(r);
    return r.blob();
  },
  async start(id: string) {
    return json(await fetch(`${BASE}/${id}/install-started`, { method: 'POST', headers: headers(false) }));
  },
  async report(id: string, report: InstallReport) {
    return json(await fetch(`${BASE}/${id}/install-result`, { method: 'POST', headers: headers(), body: JSON.stringify(report) }));
  },
};
